import React, { useState, useEffect } from "react";
import styles from "./blood_pressure.module.css";
import ElementTitle from "ui/crfs/element_title";
import DividerLine from "ui/crfs/divider_line";
import BloodPressureUI from "./components/blood_pressure_ui";
import DataCompleteChangeModal from "ui/crfs/data_complete_change_modal";
import EntryRuleOverrideModal from "ui/crfs/entry_rule_override_modal";
import ElementMenu from "ui/crfs/element_menu";
import LoadingButton from "@mui/lab/LoadingButton";

const systolic_position = "value";
const diastolic_position = "value2";

let tempStorage = null;

export default function BloodPressure(props) {
  const {
    field,
    field_id,
    title,
    onUpdate,
    enableQuery,
    read_only,
    crfStatus,
    toggleHistory,
    selected_audit_icon,
    setQueryModalData,
  } = props;

  const [localVal, setLocalVal] = useState((field && field.value_obj) || {});
  const [showingCompleteChangeModal, setShowingCompleteChangeModal] = useState(false);
  const [showingEntryOverrideModal, setShowingEntryOverrideModal] = useState(false);
  const [dataChange, setDataChange] = useState(false);

  useEffect(() => {
    tempStorage = field.value_obj;
  }, []);

  useEffect(() => {
    setLocalVal(field.value_obj);
  }, [field.value_obj]);

  const commonUpdateValueActions = (value, reason = null) => {
    let obj = { ...localVal };
    delete obj.missing;

    obj[systolic_position] = value[systolic_position];
    obj[diastolic_position] = value[diastolic_position];
    obj.value_name = field.value_name;
    obj.value2_name = field.value2_name;
    if (reason) {
      obj.reason = reason;
    }
    setLocalVal(obj);
    onUpdate(obj);
  };

  const updateValue = (error_override = false) => {
    if (entry_rule_error()) {
      if (field.entry_rule_override && !error_override) {
        setShowingEntryOverrideModal(true);
      } else if (error_override) {
        commonUpdateValueActions(localVal);
        setDataChange(false);
      } else {
        setLocalVal(tempStorage);
        setDataChange(false);
      }
    } else {
      commonUpdateValueActions(localVal);
      setDataChange(false);
    }
  };

  const onChange = (obj) => {
    if (!dataChange) {
      tempStorage = localVal;
    }
    setDataChange(true);
    setLocalVal(obj);
  };

  const acceptTempStorage = (reason) => {
    commonUpdateValueActions(localVal, reason);
    tempStorage = localVal;
    setShowingCompleteChangeModal(false);
    setShowingEntryOverrideModal(false);
    setDataChange(false);
  };
  const closeModal = () => {
    setShowingCompleteChangeModal(false);
    setShowingEntryOverrideModal(false);
    setLocalVal(tempStorage);
    setDataChange(false);
  };

  const entry_rule_error = () => {
    let error = false;
    if ((localVal.value || localVal.value2) && field.entry_rules && dataChange) {
      field.entry_rules.some((rule) => {
        if (rule.rule_type === "min") {
          if (parseInt(localVal.value) < parseInt(rule.value) || parseInt(localVal.value2) < parseInt(rule.value)) {
            error = `Must be greater than or equal to ${rule.value}`;
            return error;
          }
        } else if (rule.rule_type === "max") {
          if (parseInt(localVal.value) > parseInt(rule.value) || parseInt(localVal.value2) > parseInt(rule.value)) {
            error = `Must be less than or equal to ${rule.value}`;
            return error;
          }
        }
      });
    }
    return error;
  };

  const currentValueDisplay =
    `${localVal[systolic_position] || "--"}/${localVal[diastolic_position] || "--"}` + " mmHg";

  return (
    <div className={styles.outer_wrapper}>
      <div className={styles.input_flex}>
        <div className={styles.left_split}>
          {enableQuery ? (
            <div>
              <ElementTitle required={field.required} title={title} />
            </div>
          ) : (
            <div>{title}</div>
          )}
        </div>

        <div className={styles["right-split"]}>
          {read_only || field.read_only ? (
            <React.Fragment>
              <div>
                {currentValueDisplay}
                {field.value_obj?.missing ? <div className={styles.missing}>missing</div> : null}
              </div>
              <div className={styles.audit_icon}>
                <ElementMenu
                  {...props}
                  field={field}
                  selected={selected_audit_icon}
                  setQueryModalData={setQueryModalData}
                />
              </div>
            </React.Fragment>
          ) : (
            <div className={styles.flex_column}>
              <div className={styles.flex_row}>
                <BloodPressureUI
                  {...props}
                  field={{ value_obj: localVal }}
                  onChange={onChange}
                  onUpdate={() => {
                    updateValue();
                  }}
                  systolic_position={systolic_position}
                  diastolic_position={diastolic_position}
                  error={dataChange && entry_rule_error()}
                />
                <div className={styles.audit_icon}>
                  <ElementMenu {...props} />
                </div>
              </div>
              {field.value_obj?.missing ? <div className={styles.missing}>missing</div> : null}
              <div className={styles.flex_row}>
                {entry_rule_error().constructor === String ? (
                  <div className={styles.input_flex}>
                    <div className={styles.error}>{entry_rule_error()}</div>{" "}
                    {field.entry_rule_override ? (
                      <LoadingButton
                        variant="outlined"
                        loading={false}
                        onClick={() => updateValue(true)}
                        sx={{
                          fontSize: 14,
                          padding: "2px",
                          paddingLeft: "10px",
                          paddingRight: "10px",
                          marginLeft: "10px",
                        }}
                      >
                        Save Anyway
                      </LoadingButton>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </div>
          )}
        </div>
      </div>
      <DividerLine />
      <DataCompleteChangeModal isOpen={showingCompleteChangeModal} close={closeModal} onYes={acceptTempStorage} />
      <EntryRuleOverrideModal isOpen={showingEntryOverrideModal} close={closeModal} onYes={acceptTempStorage} />
    </div>
  );
}
